/**
 * usePushSubscription Hook
 *
 * Manages Web Push notification subscription lifecycle.
 * Handles service worker registration and push subscription management.
 * Also handles FCM token registration for Android TWA apps.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToPush, unsubscribeFromPush, registerFcmToken } from '../api/client';

// VAPID public key from environment
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

// FCM token storage key
const FCM_TOKEN_STORAGE_KEY = 'firewatch_fcm_token';
const FCM_TOKEN_REGISTERED_KEY = 'firewatch_fcm_token_registered';
const FCM_LAST_REGISTRATION_KEY = 'firewatch_fcm_last_registration';

// Re-register FCM token every 24 hours to handle token expiration
const FCM_REGISTRATION_INTERVAL_MS = 24 * 60 * 60 * 1000;

// Max retry attempts for registration
const FCM_MAX_RETRY_ATTEMPTS = 3;
const FCM_RETRY_DELAY_MS = 5000;

/**
 * Check if running inside Android TWA (Trusted Web Activity).
 * Uses localStorage for persistence across sessions.
 */
function isTWA(): boolean {
  // Check URL parameter set by the Android app
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('is_twa') === 'true') {
    // Store for future reference
    localStorage.setItem('is_twa', 'true');
    return true;
  }
  // Check localStorage (persists across sessions)
  return localStorage.getItem('is_twa') === 'true';
}

/**
 * Get FCM token from URL parameters (passed by Android app).
 */
function getFcmTokenFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('fcm_token');
}

/**
 * Store FCM token and TWA status for later use.
 * Uses localStorage for persistence across app restarts.
 */
function storeFcmToken(token: string): void {
  localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
  localStorage.setItem('is_twa', 'true');
  console.log('[Push] FCM token stored in localStorage');
}

/**
 * Get stored FCM token.
 */
function getStoredFcmToken(): string | null {
  return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
}

/**
 * Check if FCM token was already registered with the backend.
 * We only track this per-token to handle token refreshes.
 */
function isFcmTokenRegistered(token: string): boolean {
  const registered = localStorage.getItem(FCM_TOKEN_REGISTERED_KEY);
  return registered === token;
}

/**
 * Mark FCM token as registered with the backend.
 */
function markFcmTokenRegistered(token: string): void {
  localStorage.setItem(FCM_TOKEN_REGISTERED_KEY, token);
  localStorage.setItem(FCM_LAST_REGISTRATION_KEY, Date.now().toString());
}

/**
 * Check if FCM token needs re-registration (24-hour interval).
 * Always re-register if never registered or interval exceeded.
 */
function needsFcmReregistration(token: string): boolean {
  const registered = localStorage.getItem(FCM_TOKEN_REGISTERED_KEY);
  if (registered !== token) {
    return true; // Different token, needs registration
  }

  const lastRegistration = localStorage.getItem(FCM_LAST_REGISTRATION_KEY);
  if (!lastRegistration) {
    return true; // No timestamp, needs registration
  }

  const elapsed = Date.now() - parseInt(lastRegistration, 10);
  return elapsed > FCM_REGISTRATION_INTERVAL_MS;
}

/**
 * Sleep utility for retry delays.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

interface PushSubscriptionState {
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  isTWA: boolean;
  fcmToken: string | null;
  fcmRegistered: boolean;
  fcmRegistering: boolean;
  fcmLastError: string | null;
  fcmLastRegistrationTime: number | null;
}

interface UsePushSubscriptionReturn extends PushSubscriptionState {
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  requestPermission: () => Promise<NotificationPermission>;
  registerFcmManually: () => Promise<boolean>;
}

// Convert base64 string to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
}

export function usePushSubscription(): UsePushSubscriptionReturn {
  const { isAuthenticated, getAccessToken } = useAuth();
  const fcmRegistrationInProgress = useRef(false);

  const [state, setState] = useState<PushSubscriptionState>({
    isSupported: false,
    permission: 'default',
    isSubscribed: false,
    isLoading: false,
    error: null,
    isTWA: false,
    fcmToken: null,
    fcmRegistered: false,
    fcmRegistering: false,
    fcmLastError: null,
    fcmLastRegistrationTime: null,
  });

  // Check for FCM token from URL on mount and store it
  useEffect(() => {
    const urlToken = getFcmTokenFromUrl();
    if (urlToken) {
      console.log('[Push] FCM token received from Android app');
      storeFcmToken(urlToken);
      setState((prev) => ({
        ...prev,
        isTWA: true,
        fcmToken: urlToken,
        fcmRegistered: isFcmTokenRegistered(urlToken),
      }));

      // Clean up URL (remove token from URL for security)
      const url = new URL(window.location.href);
      url.searchParams.delete('fcm_token');
      url.searchParams.delete('is_twa');
      window.history.replaceState({}, '', url.toString());
    } else {
      // Check for stored token
      const storedToken = getStoredFcmToken();
      if (storedToken) {
        setState((prev) => ({
          ...prev,
          isTWA: isTWA(),
          fcmToken: storedToken,
          fcmRegistered: isFcmTokenRegistered(storedToken),
        }));
      } else {
        setState((prev) => ({
          ...prev,
          isTWA: isTWA(),
        }));
      }
    }
  }, []);

  // Core FCM registration function with retries
  const performFcmRegistration = useCallback(
    async (token: string, _isManual: boolean = false): Promise<boolean> => {
      // Prevent concurrent registrations
      if (fcmRegistrationInProgress.current) {
        console.log('[Push] FCM registration already in progress');
        return false;
      }

      fcmRegistrationInProgress.current = true;
      setState((prev) => ({ ...prev, fcmRegistering: true, fcmLastError: null }));

      let lastError: string | null = null;

      for (let attempt = 1; attempt <= FCM_MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          const authToken = await getAccessToken();
          if (!authToken) {
            lastError = 'Not authenticated';
            console.warn(`[Push] FCM registration attempt ${attempt}: No auth token`);
            if (attempt < FCM_MAX_RETRY_ATTEMPTS) {
              await sleep(FCM_RETRY_DELAY_MS * attempt);
              continue;
            }
            break;
          }

          console.log(`[Push] FCM registration attempt ${attempt}/${FCM_MAX_RETRY_ATTEMPTS}...`);
          await registerFcmToken(token, undefined, authToken);
          markFcmTokenRegistered(token);

          const lastRegTime = Date.now();
          setState((prev) => ({
            ...prev,
            fcmRegistered: true,
            fcmRegistering: false,
            fcmLastError: null,
            fcmLastRegistrationTime: lastRegTime,
          }));

          console.log('[Push] FCM token registered successfully');
          fcmRegistrationInProgress.current = false;
          return true;
        } catch (e) {
          lastError = e instanceof Error ? e.message : 'Registration failed';
          console.error(`[Push] FCM registration attempt ${attempt} failed:`, e);

          if (attempt < FCM_MAX_RETRY_ATTEMPTS) {
            // Exponential backoff
            await sleep(FCM_RETRY_DELAY_MS * attempt);
          }
        }
      }

      // All attempts failed
      setState((prev) => ({
        ...prev,
        fcmRegistering: false,
        fcmLastError: lastError || 'Registration failed after retries',
      }));

      fcmRegistrationInProgress.current = false;
      console.error('[Push] FCM registration failed after all retries');
      return false;
    },
    [getAccessToken]
  );

  // Auto-register FCM token when user is authenticated
  // Re-registers every 24 hours to handle token expiration
  useEffect(() => {
    const registerFcm = async () => {
      if (!isAuthenticated || !state.fcmToken) {
        return;
      }

      // Check if registration is needed (new token or 24h interval)
      if (!needsFcmReregistration(state.fcmToken)) {
        // Token is registered and recent - just update state
        const lastReg = localStorage.getItem(FCM_LAST_REGISTRATION_KEY);
        setState((prev) => ({
          ...prev,
          fcmRegistered: true,
          fcmLastRegistrationTime: lastReg ? parseInt(lastReg, 10) : null,
        }));
        return;
      }

      // Perform registration
      await performFcmRegistration(state.fcmToken);
    };

    registerFcm();
  }, [isAuthenticated, state.fcmToken, performFcmRegistration]);

  // Manual registration function for user-triggered re-registration
  const registerFcmManually = useCallback(async (): Promise<boolean> => {
    if (!state.fcmToken) {
      console.warn('[Push] No FCM token available for manual registration');
      return false;
    }

    if (!isAuthenticated) {
      console.warn('[Push] Must be authenticated for FCM registration');
      return false;
    }

    console.log('[Push] Manual FCM registration triggered');
    return performFcmRegistration(state.fcmToken, true);
  }, [state.fcmToken, isAuthenticated, performFcmRegistration]);

  // Check if push is supported and get current state
  useEffect(() => {
    const checkSupport = async () => {
      // Check browser support
      const isSupported =
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;

      if (!isSupported) {
        setState((prev) => ({ ...prev, isSupported: false }));
        return;
      }

      // Get current permission status
      const permission = Notification.permission;

      // Check if already subscribed
      let isSubscribed = false;
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        isSubscribed = !!subscription;
      } catch (e) {
        console.error('Failed to check push subscription:', e);
      }

      setState((prev) => ({
        ...prev,
        isSupported,
        permission,
        isSubscribed,
      }));
    };

    checkSupport();
  }, []);

  // Register service worker on mount
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });
        console.log('[Push] Service worker registered:', registration.scope);
      } catch (e) {
        console.error('[Push] Service worker registration failed:', e);
      }
    };

    registerSW();
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!state.isSupported) {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      setState((prev) => ({ ...prev, permission }));
      return permission;
    } catch (e) {
      console.error('Failed to request permission:', e);
      return 'denied';
    }
  }, [state.isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported || !isAuthenticated) {
      return false;
    }

    if (!VAPID_PUBLIC_KEY) {
      console.error('[Push] VAPID public key not configured');
      setState((prev) => ({
        ...prev,
        error: 'Push notifications not configured',
      }));
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Request permission if needed
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
        setState((prev) => ({ ...prev, permission }));
      }

      if (permission !== 'granted') {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'Notification permission denied',
        }));
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Send subscription to backend
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const subscriptionJSON = subscription.toJSON();
      await subscribeToPush(
        {
          endpoint: subscriptionJSON.endpoint!,
          keys: subscriptionJSON.keys as { p256dh: string; auth: string },
        },
        token
      );

      setState((prev) => ({
        ...prev,
        isLoading: false,
        isSubscribed: true,
        error: null,
      }));

      console.log('[Push] Successfully subscribed to Web Push');
      return true;
    } catch (e) {
      console.error('[Push] Failed to subscribe:', e);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: e instanceof Error ? e.message : 'Failed to subscribe',
      }));
      return false;
    }
  }, [state.isSupported, isAuthenticated, getAccessToken]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) {
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Remove from backend
        const token = await getAccessToken();
        if (token && subscription.endpoint) {
          try {
            await unsubscribeFromPush(subscription.endpoint, token);
          } catch (e) {
            // Backend unsubscribe may fail if already removed, that's okay
            console.warn('[Push] Backend unsubscribe failed:', e);
          }
        }
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        isSubscribed: false,
        error: null,
      }));

      console.log('[Push] Successfully unsubscribed');
      return true;
    } catch (e) {
      console.error('[Push] Failed to unsubscribe:', e);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: e instanceof Error ? e.message : 'Failed to unsubscribe',
      }));
      return false;
    }
  }, [state.isSupported, getAccessToken]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    requestPermission,
    registerFcmManually,
  };
}
